import React, { useEffect, useState } from 'react';

interface MailWindowProps {
  isOpen: boolean;
  isMaximized: boolean;
  onClose: () => void;
  onMaximize: () => void;
  onMinimize: () => void;
  zIndex: number;
  onClick: () => void;
}

/**
 * Mail contact window with copy-to-clipboard and mailto link options.
 * @param {MailWindowProps} props - Standard window props (open, maximize, minimize, close, zIndex, onClick).
 */
const MailWindow: React.FC<MailWindowProps> = ({ isOpen, isMaximized, onClose, onMaximize, onMinimize, zIndex, onClick }) => {
  const [copied, setCopied] = useState(false);
  const email = 'j@kezhao.exposed';

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = email;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleOpenEmailApp = () => {
    window.open(`mailto:${email}`, '_self');
  };

  if (!isOpen) return null;

  return (
    <div className={`window-overlay mail ${isMaximized ? 'maximized' : ''}`} style={{ zIndex }}>
      <div className={`window mail-window ${isMaximized ? 'maximized' : ''}`} onClick={onClick}>
        <div className="window-titlebar">
          <div className="window-title">
            <span className="window-icon">📧</span>
            Mail - Contact Jake
          </div>
          <div className="window-controls">
            <button className="window-control minimize" onClick={onMinimize}>−</button>
            <button className="window-control maximize" onClick={onMaximize}>
              {isMaximized ? '🗗' : '⬜'}
            </button>
            <button className="window-control close" onClick={onClose}>✕</button>
          </div>
        </div>
        <div className="window-content">
          <div className="mail-content">
            <div className="mail-header">
              <div className="mail-icon-large">📧</div>
              <div className="mail-info">
                <h2>Get in Touch</h2>
                <p className="mail-subtitle">Ready to connect? Here are your options:</p>
              </div>
            </div>

            <div className="contact-options">
              <div className="contact-card">
                <div className="contact-icon">📋</div>
                <div className="contact-details">
                  <h3>Copy Email Address</h3>
                  <p className="email-address">{email}</p>
                  <button 
                    className={`contact-button primary ${copied ? 'copied' : ''}`}
                    onClick={handleCopyEmail}
                  >
                    {copied ? (
                      <>
                        <span>✅ Copied!</span>
                      </>
                    ) : (
                      <>
                        <span>📋 Copy Email</span>
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div className="contact-card">
                <div className="contact-icon">📮</div>
                <div className="contact-details">
                  <h3>Open Email App</h3>
                  <p>Launch your default email client</p>
                  <button 
                    className="contact-button secondary"
                    onClick={handleOpenEmailApp}
                  >
                    📮 Send Email
                  </button>
                </div>
              </div>
            </div>

            <div className="mail-footer">
              <div className="footer-note">
                <p>💡 <strong>Pro tip:</strong> I typically respond within 24 hours!</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MailWindow;